import React, { useState, useMemo } from "react";
import { Modal, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

/**
 * تنبيه تصيّد ثنائي اللغة — نافذة حوار (Dialog).
 *
 * Props:
 *   showActionLinks — عرض أزرار تغيير كلمة المرور واستعادة الحساب (للمستخدمين المسجّلين).
 *
 * التحكّم بالظهور:
 *   REACT_APP_PHISHING_ALERT_UNTIL — إن لم يُضبط يظهر دائماً (fail-safe).
 *   إن ضُبط تاريخ ISO وانقضى يختفي تلقائياً بدون نشر جديد.
 *
 * الإخفاء:
 *   يُحفظ الإغلاق في sessionStorage — يعود مع كل جلسة جديدة.
 *   زر إعادة الفتح يظهر دائماً أسفل المكوّن.
 */
const PhishingAlert = ({ showActionLinks = false, logo }) => {
  const { t, i18n } = useTranslation();

  const until = process.env.REACT_APP_PHISHING_ALERT_UNTIL;
  const untilTs = until ? Date.parse(until) : NaN;
  const active = !until || (!Number.isNaN(untilTs) && Date.now() <= untilTs);

  const dismissKey = useMemo(
    () => `phishingAlert:${until || "permanent"}:dismissed`,
    [until]
  );

  const [show, setShow] = useState(() => {
    if (!active) return false;
    try {
      return !sessionStorage.getItem(dismissKey);
    } catch {
      return true;
    }
  });

  if (!active) return null;

  const dir = i18n.language === "ar" ? "rtl" : "ltr";

  const officialDomain =
    process.env.REACT_APP_SECURITY_NOTICE_DOMAIN ||
    (typeof window !== "undefined" ? window.location.origin : "");
  const supportEmail = process.env.REACT_APP_SUPPORT_EMAIL || "";
  const supportPhone = process.env.REACT_APP_SUPPORT_PHONE || "";

  const close = () => {
    setShow(false);
    try {
      sessionStorage.setItem(dismissKey, "1");
    } catch {
      /* ignore */
    }
  };

  return (
    <>
      <Modal show={show} onHide={close} centered size="lg" backdrop="static" dir={dir}>
        <Modal.Header closeButton className="bg-danger text-white">
          <Modal.Title className="d-flex align-items-center gap-2">
            {logo && (
              <img
                src={logo}
                alt="I Care Net"
                style={{
                  height: "36px",
                  width: "auto",
                  borderRadius: "4px",
                  background: "#fff",
                  padding: "2px",
                }}
              />
            )}
            <span>
              <i className="fa fa-exclamation-triangle me-2 ms-2"></i>
              {t(
                "phishing_alert_title",
                "Security Warning: Emails impersonating technical support"
              )}
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body dir={dir}>
          {/* ما حدث */}
          <p>
            {t(
              "phishing_alert_summary",
              "We have detected fraudulent emails sent from an address similar to our official support address, asking users to send their account password and transaction password. These emails are fake."
            )}
          </p>

          {/* القواعد الأساسية */}
          <div className="alert alert-warning">
            <p className="fw-bold mb-1">
              <i className="fa fa-ban me-1 ms-1"></i>
              {t(
                "phishing_alert_never_ask",
                "Our support team will never ask you for your account password, transaction password, or verification code via email, WhatsApp, phone, or any other channel."
              )}
            </p>
            <p className="mb-0">
              {t(
                "phishing_alert_no_reply",
                "Do not send any sensitive information and do not open any links included in suspicious emails."
              )}
            </p>
          </div>

          {/* النطاق الرسمي */}
          <p>
            {t(
              "phishing_alert_official_domain",
              "The only official website for managing your account is:"
            )}{" "}
            <strong>{officialDomain}</strong>
          </p>

          {/* بيانات الدعم الرسمية */}
          {(supportEmail || supportPhone) && (
            <p>
              {t("phishing_alert_official_support", "Official support contacts:")}{" "}
              {supportEmail && (
                <span>
                  {t("email", "Email")}:{" "}
                  <strong>{supportEmail}</strong>
                </span>
              )}
              {supportEmail && supportPhone ? " — " : ""}
              {supportPhone && (
                <span>
                  {t("phone", "Phone")}:{" "}
                  <strong>{supportPhone}</strong>
                </span>
              )}
            </p>
          )}

          <hr />

          {/* ماذا تفعل إن أرسلت بياناتك */}
          <h6 className="text-danger">
            <i className="fa fa-exclamation-circle me-1 ms-1"></i>
            {t(
              "phishing_alert_if_sent_title",
              "If you have already sent your information:"
            )}
          </h6>
          <ol>
            <li>
              {t(
                "phishing_alert_if_sent_step1",
                "Change your account password and transaction password immediately from your Profile page."
              )}
            </li>
            <li>
              {t(
                "phishing_alert_if_locked",
                "If you cannot log in because your credentials have been changed, use Account Recovery to regain access, or contact support immediately."
              )}
            </li>
          </ol>

          {/* أزرار عملية — للمستخدمين المسجّلين */}
          {showActionLinks && (
            <div className="d-flex flex-wrap gap-2 mt-3">
              <Link to="/profile" className="btn btn-primary btn-sm" onClick={close}>
                <i className="fa fa-key me-1 ms-1"></i>
                {t("phishing_alert_change_password_cta", "Change passwords")}
              </Link>
              <Link to="/account-recovery" className="btn btn-outline-secondary btn-sm" onClick={close}>
                <i className="fa fa-life-ring me-1 ms-1"></i>
                {t("phishing_alert_recovery_cta", "Account recovery")}
              </Link>
            </div>
          )}

          {/* تلميح نصي — لصفحة الدخول (المستخدم غير مسجل) */}
          {!showActionLinks && (
            <div className="mt-3 text-muted" style={{ fontSize: "0.9rem" }}>
              <p className="mb-1">
                {t(
                  "phishing_alert_login_hint",
                  "After logging in, go to your Profile to change your passwords. If you cannot log in, use the Account Recovery page."
                )}
              </p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={close}>
            {t("close", "Close")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* زر لإعادة فتح النافذة — يظهر دائماً */}
      <div className="text-center mt-2 mb-2" dir={dir}>
        <button
          type="button"
          className="btn btn-link p-0 text-danger"
          style={{ fontSize: "0.85rem" }}
          onClick={() => setShow(true)}
        >
          <i className="fa fa-exclamation-triangle me-1 ms-1"></i>
          {t(
            "phishing_alert_reopen",
            "Security warning: phishing emails detected — click for details"
          )}
        </button>
      </div>
    </>
  );
};

export default PhishingAlert;
